<template>
  <v-dialog
    fullscreen
    v-model="isOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    transition="slide-y-reverse-transition"
    aria-labelledby="asset-selection-title">
    <v-card class="dialog-list">
      <v-card-title id="asset-selection-title" class="dialog-list__title">
        <h2 class="typography-heading-md-bold">Pick asset:</h2>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="closeDialog"
          :aria-label="t('global.basicTerms.close')">
          <v-icon icon="mdi-close"></v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 d-flex flex-column flex-1 min-h-0 overflow-hidden">
        <v-skeleton-loader v-if="isLoading" type="list-item-three-line" />

        <div v-else class="d-flex flex-column gap-1 flex-1 min-h-0 overflow-hidden">
          <div class="d-flex flex-column justify-start gap-1 px-5 flex-shrink-0">
            <v-text-field
              hide-details
              v-model="search"
              :placeholder="t('global.basicTerms.search')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              color="primary"
              class="flex-grow-1" />
            <v-checkbox
              v-model="showDisabled"
              label="Show disabled"
              density="compact"
              hide-details
              class="flex-shrink-0 typography-body-md text--color-text-secondary" />
          </div>
          <div class="flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
            <div
              v-if="hasNoAssets"
              class="d-flex flex-column align-center justify-center pa-8 flex-1">
              <p class="typography-body-md text--color-text-secondary text-center">
                No assets available
              </p>
            </div>
            <v-list v-else class="dialog-list__list">
              <v-list-item
                v-for="(asset, index) in assets"
                :key="asset.assetId || index"
                :disabled="asset.disabled"
                variant="plain"
                color="#C96500"
                class="dialog-list__item"
                :class="{ disabled: asset.disabled }"
                @click="handleAssetClick(asset)">
                <template #prepend>
                  <v-icon size="20" :icon="asset.icon"></v-icon>
                </template>

                <template #title>
                  <div class="d-flex flex-row gap-1 align-center">
                    <span class="typography-body-lg">{{ asset.assetCode }}</span>
                  </div>
                </template>

                <template #append v-if="asset.spotAreaName || asset.spotCode">
                  <yard-spot-presenter
                    :spot="{
                      id: asset.spotId || '',
                      code: asset.spotCode || ''
                    }"
                    :area="{
                      name: asset.spotAreaName || ''
                    }" />
                </template>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ComputedRef, Ref } from 'vue';

import { useDialogListener, useEventHub, useYardAssetSelect } from '@/composables';
import { useWarehouseStore } from '@/store/useWarehouseStore';
import YardSpotPresenter from '@satellite/components/elements/presenters/yard/YardSpotPresenter.vue';

const ASSET_EVENTS = {
  CREATE: 'create-Asset',
  DELETE: 'delete-Asset',
  UPDATE: 'update-Asset'
} as const;

type AssetItem = {
  assetId: string;
  assetCode: string;
  icon: string;
  isAssigned: boolean;
  spotAreaName: string | null;
  spotCode: string | null;
  spotId: string | null;
  disabled: boolean;
};

export type IYardAssetSelect = {
  assetsRef: ComputedRef<AssetItem[]>;
  isLoadingRef: Ref<boolean>;
  fetchAll: () => void;
};

const { t } = useI18n();
const search = ref('');
const showDisabled = ref(true);
const warehouseStore = useWarehouseStore();
const eventHub = useEventHub();
const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-select-asset-dialog');

const yardAsset = useYardAssetSelect({
  warehouseId: warehouseStore.selectedWarehouseId,
  search: () => search.value,
  showDisabled: () => showDisabled.value,
  includeWaitingAssignmentAssets: () => dialogProps.value?.includeWaitingAssignmentAssets ?? false,
  includeAssignedAssets: () => dialogProps.value?.includeAssignedAssets ?? false,
  includeAssociatedToActiveMoveTasks: () =>
    dialogProps.value?.includeAssociatedToActiveMoveTasks ?? false
});

const isOpen = computed(() => isDialogOpen.value);
const isLoading = computed(() => yardAsset.isLoadingRef.value);
const assets = computed(() => yardAsset.assetsRef?.value || []);
const hasNoAssets = computed(() => assets.value.length === 0);

const handleAssetClick = (asset: AssetItem) => {
  if (!asset || asset.disabled || !asset.spotId) {
    return;
  }

  dialogProps.value?.onAssetSelected?.(asset.assetId, asset.spotId);
  closeDialog();
};

watch(
  () => isDialogOpen.value,
  newVal => {
    if (newVal) {
      yardAsset.fetchAll();
    }
  }
);

const handleAssetChange = () => {
  yardAsset.fetchAll();
};

onMounted(() => {
  subscribeToDialogEvent();

  eventHub.on(ASSET_EVENTS.CREATE, handleAssetChange);
  eventHub.on(ASSET_EVENTS.DELETE, handleAssetChange);
  eventHub.on(ASSET_EVENTS.UPDATE, handleAssetChange);
});

onBeforeUnmount(() => {
  unsubscribeFromDialogEvent();

  eventHub.off(ASSET_EVENTS.CREATE, handleAssetChange);
  eventHub.off(ASSET_EVENTS.DELETE, handleAssetChange);
  eventHub.off(ASSET_EVENTS.UPDATE, handleAssetChange);
});
</script>

<style lang="scss" scoped>
:deep(.v-card) {
  height: calc(100dvh - $spacing-6) !important;
  min-height: calc(100dvh - $spacing-6) !important;
  max-height: calc(100dvh - $spacing-6) !important;
  top: $spacing-6;
  margin: 0;
  display: flex;
  flex-direction: column;
}

:deep(.v-card__title) {
  flex-shrink: 0;
}

:deep(.v-card__text) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
